import type { ReactNode, Ref } from "react";
import { useCallback, useImperativeHandle, useRef, useState } from "react";

import { Form } from "@/components";

interface SettingsStepProps {
    name: string;
    onChangeState?: (newState: Record<string, unknown>) => void;
    onSave?: () => void;
    state?: Record<string, unknown>;
    renderFormControls?: (args: {
        formState: Record<string, unknown>;
        changeFormState: (newState: Record<string, unknown>) => void;
        FormControl: typeof Form.FormControl;
    }) => ReactNode;
    ref?: Ref<unknown>;
}

/**
 * Wizard step hosting a form whose controls are supplied by `renderFormControls`.
 *
 * @param root0 - The component props. `ref` exposes `validate()`.
 * @returns The rendered settings step.
 */
const SettingsStep = ({
    state = {},
    onChangeState,
    renderFormControls,
    ref,
}: SettingsStepProps) => {
    const [formState, setFormState] = useState({ ...state });
    const formRef = useRef(null);

    useImperativeHandle(
        ref,
        () => ({
            validate: () => formRef.current?.validate(),
        }),
        [],
    );

    const changeFormState = useCallback(
        (newState) => {
            setFormState((prevState) => ({ ...prevState, ...newState }));
            onChangeState?.(newState);
        },
        [onChangeState],
    );

    return (
        <Form ref={formRef} hideSubmit model={formState}>
            {renderFormControls?.({
                formState,
                changeFormState,
                FormControl: Form.FormControl,
            })}
        </Form>
    );
};

export default SettingsStep;
